import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getSharedQuiz } from "../lib/api";
import { Loader } from "../components/Shared";
import { Check, X, ArrowRight } from "lucide-react";

export default function ShareQuiz() {
  const { id } = useParams();
  const [quiz, setQuiz] = useState(null);
  const [err, setErr] = useState("");
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    getSharedQuiz(id).then(setQuiz).catch(() => setErr("Quiz not found."));
  }, [id]);

  const score = quiz
    ? quiz.questions.reduce((s, q, i) => s + (answers[i] === q.correct_index ? 1 : 0), 0)
    : 0;

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-10 md:py-14" data-testid="share-quiz-page">
      {err && <div className="text-[#D9534F]" data-testid="share-quiz-error">{err}</div>}
      {!quiz && !err && <Loader label="Loading quiz…" />}
      {quiz && (
        <div className="space-y-5 fadeIn" data-testid="share-quiz-content">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold">Shared quiz from EduSphere</div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">{quiz.topic}</h1>
          </div>
          {quiz.questions.map((q, qi) => (
            <div key={qi} className="card p-6">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#3A5A40] font-semibold mb-2">Question {qi + 1}</div>
              <div className="font-heading text-lg font-semibold text-[#1A1A1A]">{q.question}</div>
              <div className="grid sm:grid-cols-2 gap-2 mt-4">
                {q.options.map((opt, oi) => {
                  const chosen = answers[qi] === oi;
                  const correct = q.correct_index === oi;
                  let cls = "bg-white border-[#DCD7CE] hover:border-[#E07A5F]";
                  if (submitted) {
                    if (correct) cls = "bg-[#EAECE6] border-[#3A5A40]";
                    else if (chosen && !correct) cls = "bg-[#FBE7DF] border-[#D9534F]";
                  } else if (chosen) {
                    cls = "bg-[#FBE7DF] border-[#E07A5F]";
                  }
                  return (
                    <button
                      key={oi}
                      type="button"
                      disabled={submitted}
                      onClick={() => setAnswers({ ...answers, [qi]: oi })}
                      className={`text-left text-sm border rounded-xl px-4 py-3 transition flex items-center justify-between gap-2 ${cls}`}
                    >
                      <span className="text-[#1A1A1A]">{opt}</span>
                      {submitted && correct && <Check size={16} className="text-[#3A5A40]" />}
                      {submitted && chosen && !correct && <X size={16} className="text-[#D9534F]" />}
                    </button>
                  );
                })}
              </div>
              {submitted && (
                <div className="text-sm text-[#4A4A4A] mt-3">
                  <span className="font-semibold text-[#1A1A1A]">Why: </span>{q.explanation}
                </div>
              )}
            </div>
          ))}
          {!submitted ? (
            <button
              onClick={() => setSubmitted(true)}
              disabled={Object.keys(answers).length !== quiz.questions.length}
              className="btn-secondary disabled:opacity-50"
              data-testid="share-quiz-submit"
            >
              Submit ({Object.keys(answers).length}/{quiz.questions.length})
            </button>
          ) : (
            <div className="card p-6 flex items-center justify-between flex-wrap gap-3" data-testid="share-quiz-score">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#3A5A40] font-semibold">Your score</div>
                <div className="font-heading text-3xl font-bold">{score} / {quiz.questions.length}</div>
              </div>
              <Link to="/quiz" className="btn-primary inline-flex items-center gap-2">
                Make your own <ArrowRight size={14} />
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
